import { PLANS, isPaidPlan, type PlanId } from "@/lib/plans";

/**
 * Single source of truth for a subscription's MONTHLY CREDIT ALLOCATION — the
 * number of build credits the plan grants (and re-grants every cycle), which
 * every plan/credit surface should display alongside the tier.
 *
 * This is distinct from every other credit figure the UI shows:
 *   • monthly allocation — what the SUBSCRIPTION grants each cycle (this file).
 *   • total available balance — the live pool (allocation + rollover + purchased
 *     top-ups), e.g. the "6711/20160" the chip shows.
 *   • purchased / top-up credits — one-off packs bought on top of the plan.
 *   • rolled-over credits — last cycle's unspent balance carried forward.
 *   • daily free credits — the free tier's 50/day spend cap window.
 *
 * Authoritative value = `user_billing.custom_monthly_credits` when the user has
 * chosen a custom monthly total (the "upgrade your monthly credits" flow), else
 * the plan's fixed default `PLANS[plan].buildCreditsPerMonth`. Base Pro/Grow/
 * Scale subscriptions all start at 650 (custom_monthly_credits set to 650 by the
 * tiered/rated fulfillment); grandfathered flat rows have NULL custom credits and
 * fall back to their legacy plan default. Free has no subscription allocation.
 *
 * NEVER hardcode 650 (or a plan default) at a call site — resolve here so every
 * surface reads the same number and an upgraded user is never understated.
 */

export interface MonthlyAllocationInput {
  plan: PlanId | string | null | undefined;
  /** `user_billing.custom_monthly_credits`. NULL/absent → plan default. */
  customMonthlyCredits?: number | null;
}

/**
 * Resolve the effective monthly credit allocation for a subscription.
 *
 * Returns `null` for the free plan (and unknown/absent plans) — free accounts
 * run on the daily-cap model, not a monthly subscription allocation, so callers
 * should show nothing rather than a misleading number. Paid plans always return
 * a positive integer.
 */
export function resolveMonthlyAllocation(
  input: MonthlyAllocationInput,
): number | null {
  const { plan, customMonthlyCredits } = input;
  if (!isPaidPlan(plan)) return null;

  // A positive custom total (the upgraded allocation) always wins. Guard on
  // > 0 so a 0 / negative / NaN value can never zero out the displayed grant.
  if (
    typeof customMonthlyCredits === "number" &&
    Number.isFinite(customMonthlyCredits) &&
    customMonthlyCredits > 0
  ) {
    return Math.round(customMonthlyCredits);
  }

  // Legacy flat rows (NULL custom credits) fall back to the plan default.
  return PLANS[plan as PlanId].buildCreditsPerMonth;
}

/** US-style thousands separators, e.g. 3000 → "3,000". */
function groupThousands(n: number): string {
  return n.toLocaleString("en-US");
}

export interface FormatAllocationOptions {
  /**
   * Compact rendering for space-constrained surfaces (nav chip tooltips,
   * inline chips): "650/mo" instead of "650 credits/month". Still unambiguous —
   * it reads as a per-month figure and is never confused with a balance.
   */
  compact?: boolean;
}

/**
 * Format a resolved allocation for display. Returns null when there is no
 * allocation to show (free / unresolved), so a caller can render nothing.
 *
 *   full    → "650 credits/month" · "3,000 credits/month"
 *   compact → "650/mo" · "3,000/mo"
 */
export function formatMonthlyAllocation(
  allocation: number | null,
  opts: FormatAllocationOptions = {},
): string | null {
  if (allocation == null || !Number.isFinite(allocation) || allocation <= 0) {
    return null;
  }
  const n = groupThousands(Math.round(allocation));
  return opts.compact ? `${n}/mo` : `${n} credits/month`;
}

/**
 * "N of M monthly credits" style remaining-vs-allocation label, distinguishing
 * the monthly allocation from the live balance. Used by low-balance / usage
 * surfaces that want to show progress against the plan's monthly grant.
 * Returns null when there's no allocation (free plan).
 *
 *   full    → "120 of 650 monthly credits"
 *   compact → "120/650 monthly"
 */
export function formatMonthlyRemaining(
  remaining: number,
  allocation: number | null,
  opts: FormatAllocationOptions = {},
): string | null {
  if (allocation == null || !Number.isFinite(allocation) || allocation <= 0) {
    return null;
  }
  const rem = groupThousands(Math.max(0, Math.round(remaining)));
  const alloc = groupThousands(Math.round(allocation));
  return opts.compact
    ? `${rem}/${alloc} monthly`
    : `${rem} of ${alloc} monthly credits`;
}

/**
 * Convenience: resolve + format in one call for the common "Scale · 650
 * credits/month" tier-plus-allocation surfaces.
 */
export function monthlyAllocationLabel(
  input: MonthlyAllocationInput,
  opts: FormatAllocationOptions = {},
): string | null {
  return formatMonthlyAllocation(resolveMonthlyAllocation(input), opts);
}
